import useTranslation from "next-translate/useTranslation"
import Link from "next/link"
import { Button, Card, CardBody, CardHeader, Col, Collapse, Row, Spinner } from "reactstrap"

import { ICategory, IProject, ProjectState } from "@api/schema"
import Icon from "@components/common/Icon"
import TranslatedHtml from "@components/common/TranslatedHtml"
import { EntityType } from "@redux/reduxTypes"
import { useEntityCollection } from "@services/hooks/useEntityCollection"
import { useToggleState } from "@services/hooks/useToggleState"
import { IProjectFilterCriteria } from "@services/projectHelper"
import { Routes, routeWithParams } from "@services/routes"
import { uriHashMatchesId } from "@services/util"

interface IProps {
  category: ICategory
}

/**
 * This component provides a single card for displaying an (extended) category
 */
const ExtendedCategoryCard: React.FC<IProps> = (props: IProps) => {
  const { category } = props
  const { t } = useTranslation("common")

  const { isTrue: cardOpen, toggle: toggleCard } = useToggleState(uriHashMatchesId(`category-${category?.id}`))

  const { entities: categoryProjects, request: projectRequest } = useEntityCollection<IProject>(
    EntityType.Project,
    { categories: category?.id, locked: false, state: ProjectState.Active } as IProjectFilterCriteria,
    { doNotLoad: !cardOpen }
  )

  return category && <Card className="collapsable-card">
    <CardHeader
      id={`category-${category.id}`}
      onClick={toggleCard}
    >
      <span>
        <div className="extended-category__title">
          {category.subtitle?.length > 0 && <h3>{category.subtitle}</h3>}
          <Link href={`${Routes.Marketplace}?category=${category.id}`}>
            <a className="btn text-filteritem">
              {category.title}
            </a>
          </Link>
        </div>
        <div>
          {category.description?.length > 0 ? <TranslatedHtml content={category.description} /> : <br />}
        </div>
        <div className="btn-flex-layout">
          <Button className="primary-btn" onClick={toggleCard}>
            {t("page.process.categories.showMore")}
          </Button>
          <Link href={Routes.CreateIdea}>
            <a className="btn primary-btn">
              {t("page.process.categories.iHaveAnIdea")}
            </a>
          </Link>
        </div>
      </span>
      <span className={"chevron-right" + (cardOpen ? " chevron-right--toggled" : "")}><Icon name="chevron-right" size={42} /></span>
    </CardHeader>
    <Collapse isOpen={cardOpen}>
      <CardBody aria-expanded={cardOpen ? "true" : "false"}>
        <Row className="mb-4">
          {category.problems?.length > 0 && <Col>
            <h4>{t("page.process.categories.problems")}</h4>
            <ul>
              {category.problems.map((problem, index) => <li key={index}>{problem}</li>)}
            </ul>
          </Col>
          }
          <Col md={4}>
            {categoryProjects?.length > 0
              ? <>
                <h4>{t("page.process.categories.projects")}</h4>
                <ul className="cube-bullets">
                  {categoryProjects.map((project, index) => {
                    // return 10 items max
                    return index < 10 && <li key={project.id}>
                      <Link href={routeWithParams(Routes.ProjectPage, { slug: project.slug || project.id })}>
                        <a>
                          {project.name}
                        </a>
                      </Link>
                    </li>
                  })}
                </ul>
                <Link href={`${Routes.Marketplace}?category=${category.id}`}>
                  <a>
                    {t("page.process.categories.allProjects")}
                  </a>
                </Link>
                &nbsp;<Icon name="arrow-right" size={24} />
              </>
              : projectRequest?.isLoading
                ? <Spinner />
                : t("page.process.categories.noProjects")
            }
          </Col>
        </Row>
      </CardBody>
    </Collapse>
  </Card>
}

export default ExtendedCategoryCard
